import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
    ArrowLeft, User, FileText, Car, CreditCard, Activity,
    Save, Loader2, CheckCircle, XCircle, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DatePickerController } from '@/components/ui/date-picker';
import { toast } from 'sonner';

import {
    useDriver,
    useUpdatePersonalDetails,
    useUpdateVehicleDetails,
    useUpdateBankingInfo,
    useApproveKyc,
    useRejectKyc,
    useKeepInViewKyc,
    useDownloadDriverDocument,
    getDocumentTempUrl,
    useDocumentTempUrls,
} from '@/api-services/hooks/useDriver';
import { useActiveVehicleCategories } from '@/api-services/hooks/useVehicleCategory';
import { DriverDTO } from '@/api-services/hooks/adminTypes';
import { getDocumentViewUrl } from '@/api-services/config';
import KycStatusBadge from '@/components/drivers/KycStatusBadge';
import DocumentViewer from '@/components/drivers/DocumentViewer';
import KycActionsPanel from '@/components/drivers/KycActionsPanel';
import DocumentUploadField from '@/components/drivers/DocumentUploadField';

const DriverDetailsPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') || 'overview';
    const [isKycReviewMode] = useState(() => searchParams.get('tab') === 'kyc');
    const driverId = parseInt(id || '0');

    // API Hooks
    const { data: driver, loading: isLoadingDriver, refetch } = useDriver(driverId, { enabled: !!driverId });

    const { mutate: updatePersonal, isPending: isUpdatingPersonal } = useUpdatePersonalDetails();
    const { mutate: updateVehicle, isPending: isUpdatingVehicle } = useUpdateVehicleDetails();
    const { mutate: updateBanking, isPending: isUpdatingBanking } = useUpdateBankingInfo();

    const { data: vehicleCategories } = useActiveVehicleCategories() as { data: { vehicleCategoryId: number; vehicleCategoryName: string; maxPassengerCount?: number; maxLuggageVolume?: number }[] | undefined };
    const { mutateAsync: approveKyc, isPending: isApprovingKyc } = useApproveKyc();
    const { mutateAsync: rejectKyc, isPending: isRejectingKyc } = useRejectKyc();
    const { mutateAsync: keepInView, isPending: isKeepingInView } = useKeepInViewKyc();
    const { mutateAsync: downloadDoc } = useDownloadDriverDocument();

    // Forms
    const personalForm = useForm();
    const vehicleForm = useForm();
    const bankingForm = useForm();

    // Resolve document temp URLs — must be called unconditionally (Rules of Hooks)
    const allRawPaths = driver ? [
        driver.govtIdFrontUrl,
        driver.govtIdBackUrl,
        driver.drivingLicenseFrontUrl,
        driver.drivingLicenseBackUrl,
        driver.selfieWithIdUrl,
        driver.ownershipDocUrl || driver.vehicleRegistrationCertUrl,
        driver.insuranceDocUrl || driver.vehicleInsuranceCertUrl,
        driver.roadTaxDocUrl,
        driver.profilePhotoUrl,
    ].filter((p): p is string => !!p) : [];
    const { urlMap: tempUrlMap, loading: tempUrlsLoading } = useDocumentTempUrls(allRawPaths);

    // Populate forms when driver data loads — only depend on driver, not form objects
    useEffect(() => {
        if (!driver) return;
        personalForm.reset({
            fullName: driver.fullName,
            email: driver.email,
            phoneNumber: driver.phoneNumber,
            dateOfBirth: driver.dateOfBirth,
            gender: driver.gender,
            address: driver.address,
            city: driver.city,
            state: driver.state,
            country: driver.country,
            preferredLanguage: driver.preferredLanguage,
        });
        vehicleForm.reset({
            vehicleType: driver.vehicleType,
            vehicleMake: driver.vehicleMake,
            vehicleModel: driver.vehicleModel,
            vehicleRegistrationNumber: driver.vehicleRegistrationNumber || driver.vehicleLicensePlate,
            vehicleColor: driver.vehicleColor,
            yearOfManufacture: driver.yearOfManufacture || driver.vehicleYear,
            seatingCapacity: driver.seatingCapacity,
            luggageCapacity: driver.luggageCapacity,
        });
        bankingForm.reset({
            bankAccountHolderName: driver.bankAccountHolderName,
            bankName: driver.bankName,
            accountNumber: driver.accountNumber || driver.bankAccountNumber,
            ifscCode: driver.ifscCode || driver.bankIfscCode,
            swiftCode: driver.swiftCode,
            paymentFrequency: driver.paymentFrequency,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [driver]);

    const handleTabChange = (value: string) => {
        setSearchParams({ tab: value });
    };

    // Update Handlers (use mutate callbacks — mutate returns void, so .then() never runs)
    const onPersonalSubmit = (data: any) => {
        if (!driver) return;
        const updateData: DriverDTO = { ...driver, ...data };

        updatePersonal(
            { id: driverId, data: updateData },
            {
                onSuccess: () => {
                    toast.success('Personal details updated');
                    refetch();
                },
                onError: (err) => toast.error(`Update failed: ${err.message}`),
            }
        );
    };

    const onVehicleSubmit = (data: any) => {
        if (!driver) return;
        updateVehicle(
            {
                driverId,
                ...data,
                vehicleCategoryId: driver.vehicleCategoryId || 0,
            },
            {
                onSuccess: async () => {
                    toast.success('Vehicle details updated');
                    // Set KYC status to "Keep In View" so the driver goes back for re-review
                    try {
                        await keepInView(driverId);
                        toast.info('KYC status set to Keep In View for re-review');
                    } catch (err: any) {
                        toast.warning(`Vehicle updated but failed to update KYC status: ${err.message}`);
                    }
                    refetch();
                },
                onError: (err) => toast.error(`Update failed: ${err.message}`),
            }
        );
    };

    const onBankingSubmit = (data: any) => {
        if (!driver) return;
        updateBanking(
            { driverId, ...data },
            {
                onSuccess: () => {
                    toast.success('Banking details updated');
                    refetch();
                },
                onError: (err) => toast.error(`Update failed: ${err.message}`),
            }
        );
    };

    // KYC Actions
    const handleApproveKyc = async (id: number) => {
        try {
            await approveKyc(id);
            toast.success('KYC Approved');
            refetch();
        } catch (err: any) {
            toast.error(`Failed to approve: ${err.message}`);
        }
    };

    const handleRejectKyc = async (id: number, reason: string) => {
        try {
            await rejectKyc({ driverId: id, rejectionReason: reason });
            toast.success('KYC Rejected');
            refetch();
        } catch (err: any) {
            toast.error(`Failed to reject: ${err.message}`);
        }
    };

    const handleKeepInView = async (id: number) => {
        try {
            await keepInView(id);
            toast.success('Marked as Keep in View');
            refetch();
        } catch (err: any) {
            toast.error(`Failed to update status: ${err.message}`);
        }
    };

    const handleDownload = async (url: string, fileName: string) => {
        try {
            // The url coming in is already a resolved temp URL (http)
            // Fetch it as a blob so we can trigger a real download with the correct filename
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
            toast.success('Download started');
        } catch (err: any) {
            toast.error(`Download failed: ${err.message}`);
        }
    };

    if (isLoadingDriver && !driver) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!driver) {
        return (
            <div className="p-8 text-center">
                <h2 className="text-2xl font-bold text-destructive">Driver not found</h2>
                <Button onClick={() => navigate('/admin/drivers')} className="mt-4">
                    Back to Drivers
                </Button>
            </div>
        );
    }

    // Status shows inactive when KYC is Keep in View or Rejected; active/inactive only when KYC is Approved
    const normalizedKycStatus = (() => {
        const raw = (driver.kycStatus || '')
            .toUpperCase()
            .replace(/[\s-]+/g, '_');
        if (raw === 'KIV' || raw === 'KEEPINVIEW') return 'KEEP_IN_VIEW';
        return raw;
    })();
    const effectiveStatusIsActive =
        normalizedKycStatus !== 'KEEP_IN_VIEW' && normalizedKycStatus !== 'REJECTED' && !!driver.isActive;

    // Helper: given a raw file path, return the resolved temp URL (or undefined)
    const getResolvedUrl = (rawPath: string | null | undefined): string | undefined => {
        if (!rawPath) return undefined;
        // If it's already a full HTTP URL or data URI, use as-is
        if (rawPath.startsWith('http') || rawPath.startsWith('data:')) return rawPath;
        // Try temp URL first, otherwise use document download endpoint
        if (tempUrlMap[rawPath]) return tempUrlMap[rawPath];
        return getDocumentViewUrl(rawPath);
    };

    // Prepare documents for viewer using resolved temp URLs
    const kycDocuments = [
        { label: 'Govt ID Front', url: getResolvedUrl(driver.govtIdFrontUrl), fileName: 'govt-id-front' },
        { label: 'Govt ID Back', url: getResolvedUrl(driver.govtIdBackUrl), fileName: 'govt-id-back' },
        { label: 'Driving License Front', url: getResolvedUrl(driver.drivingLicenseFrontUrl), fileName: 'license-front' },
        { label: 'Driving License Back', url: getResolvedUrl(driver.drivingLicenseBackUrl), fileName: 'license-back' },
        { label: 'Selfie with ID', url: getResolvedUrl(driver.selfieWithIdUrl), fileName: 'selfie' },
    ].filter(d => d.url);

    const vehicleDocuments = [
        { label: 'Ownership Document', url: getResolvedUrl(driver.ownershipDocUrl || driver.vehicleRegistrationCertUrl), fileName: 'ownership-doc' },
        { label: 'Insurance Document', url: getResolvedUrl(driver.insuranceDocUrl || driver.vehicleInsuranceCertUrl), fileName: 'insurance-doc' },
        { label: 'Road Tax Document', url: getResolvedUrl(driver.roadTaxDocUrl), fileName: 'road-tax-doc' },
    ].filter(d => d.url);

    return (
        <div className="space-y-6 animate-fade-in pb-10">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center space-x-4">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/admin/drivers')}
                        className="hover:bg-muted"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>
                    <div className="flex items-center gap-3">
                        {getResolvedUrl(driver.profilePhotoUrl) ? (
                            <img
                                src={getResolvedUrl(driver.profilePhotoUrl)}
                                alt={driver.fullName}
                                className="w-12 h-12 rounded-full object-cover border-2 border-primary/10"
                            />
                        ) : (
                            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                                <User className="w-6 h-6 text-muted-foreground" />
                            </div>
                        )}
                        <div>
                            <h1 className="text-2xl font-bold text-foreground">{driver.fullName}</h1>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <span>{driver.phoneNumber}</span>
                                <span>•</span>
                                <span>{driver.email}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <KycStatusBadge status={driver.kycStatus || 'PENDING'} size="lg" />
                    <Badge variant={effectiveStatusIsActive ? 'default' : 'secondary'}>
                        {effectiveStatusIsActive ? 'Active' : 'Inactive'}
                    </Badge>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
                {isKycReviewMode ? (
                    <TabsList className="grid w-full grid-cols-2 h-auto">
                        <TabsTrigger value="kyc" className="py-2">KYC & Docs</TabsTrigger>
                        <TabsTrigger value="vehicle" className="py-2">Vehicle</TabsTrigger>
                    </TabsList>
                ) : (
                    <TabsList className="grid w-full grid-cols-2 md:grid-cols-6 h-auto">
                        <TabsTrigger value="overview" className="py-2">Overview</TabsTrigger>
                        <TabsTrigger value="personal" className="py-2">Personal</TabsTrigger>
                        <TabsTrigger value="kyc" className="py-2">KYC & Docs</TabsTrigger>
                        <TabsTrigger value="vehicle" className="py-2">Vehicle</TabsTrigger>
                        <TabsTrigger value="banking" className="py-2">Banking</TabsTrigger>
                        <TabsTrigger value="activity" className="py-2">Activity</TabsTrigger>
                    </TabsList>
                )}

                {/* Overview Tab */}
                <TabsContent value="overview">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Card className="md:col-span-2">
                            <CardHeader>
                                <CardTitle>Driver Summary</CardTitle>
                            </CardHeader>
                            <CardContent className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-muted-foreground">Full Name</p>
                                    <p className="font-medium">{driver.fullName}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Phone</p>
                                    <p className="font-medium">{driver.phoneNumber}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Email</p>
                                    <p className="font-medium">{driver.email || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Vehicle</p>
                                    <p className="font-medium">{driver.vehicleMake} {driver.vehicleModel} ({driver.vehicleRegistrationNumber})</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Total Rides</p>
                                    <p className="font-medium">{driver.totalRidesCompleted || 0}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Rating</p>
                                    <p className="font-medium flex items-center">
                                        {driver.averageRating || 'N/A'} <span className="text-yellow-500 ml-1">★</span>
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Quick Actions</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <Button variant="outline" className="w-full justify-start" onClick={() => handleTabChange('kyc')}>
                                    <FileText className="w-4 h-4 mr-2" />
                                    Review KYC
                                </Button>
                                <Button variant="outline" className="w-full justify-start" onClick={() => handleTabChange('vehicle')}>
                                    <Car className="w-4 h-4 mr-2" />
                                    Update Vehicle
                                </Button>
                                <Button variant="outline" className="w-full justify-start" onClick={() => handleTabChange('banking')}>
                                    <CreditCard className="w-4 h-4 mr-2" />
                                    Update Banking
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Personal Tab */}
                <TabsContent value="personal">
                    <Card>
                        <CardHeader>
                            <CardTitle>Personal Information</CardTitle>
                            <CardDescription>Update driver's personal details</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form id="personal-form" onSubmit={personalForm.handleSubmit(onPersonalSubmit)} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Full Name</Label>
                                        <Input {...personalForm.register('fullName')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Phone Number</Label>
                                        <Input {...personalForm.register('phoneNumber')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Email *</Label>
                                        <Input {...personalForm.register('email', { required: 'Email is required' })} />
                                    </div>
                                    <div className="space-y-2">
                                        <DatePickerController
                                            control={personalForm.control}
                                            name="dateOfBirth"
                                            label="Date of Birth"
                                            placeholder="Select date of birth"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Gender</Label>
                                        <Controller
                                            control={personalForm.control}
                                            name="gender"
                                            render={({ field }) => (
                                                <Select value={field.value || ''} onValueChange={field.onChange}>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select gender" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="MALE">Male</SelectItem>
                                                        <SelectItem value="FEMALE">Female</SelectItem>
                                                        {/* <SelectItem value="OTHER">Other</SelectItem> */}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        />
                                    </div>
                                    <div className="space-y-2 md:col-span-2">
                                        <Label>Address</Label>
                                        <Input {...personalForm.register('address')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>City</Label>
                                        <Input {...personalForm.register('city')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>State</Label>
                                        <Input {...personalForm.register('state')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Country</Label>
                                        <Input {...personalForm.register('country')} />
                                    </div>
                                </div>
                            </form>
                        </CardContent>
                        <CardFooter>
                            <Button type="submit" form="personal-form" disabled={isUpdatingPersonal}>
                                {isUpdatingPersonal && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Save Changes
                            </Button>
                        </CardFooter>
                    </Card>
                </TabsContent>

                {/* KYC Tab */}
                <TabsContent value="kyc">
                    <div className="space-y-6">
                        {(() => {
                            const requiredDocuments = [
                                { label: 'Government ID Front', uploaded: !!driver.govtIdFrontUrl },
                                { label: 'Driving License Front', uploaded: !!driver.drivingLicenseFrontUrl },
                                { label: 'Selfie with ID', uploaded: !!driver.selfieWithIdUrl },
                            ];
                            return (
                                <KycActionsPanel
                                    driverId={driverId}
                                    currentStatus={driver.kycStatus || 'PENDING'}
                                    onApprove={handleApproveKyc}
                                    onReject={handleRejectKyc}
                                    onKeepInView={handleKeepInView}
                                    missingDocuments={requiredDocuments}
                                />
                            );
                        })()}

                        <Card>
                            <CardHeader>
                                <CardTitle>KYC Documents</CardTitle>
                                <CardDescription>
                                    ID Type: <span className="font-medium">{driver.govtIdType}</span> |
                                    ID Number: <span className="font-medium">{driver.govtIdNumber}</span>
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <DocumentViewer
                                    documents={kycDocuments}
                                    onDownload={handleDownload}
                                    loading={tempUrlsLoading}
                                    hideActions
                                />
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Vehicle Tab */}
                <TabsContent value="vehicle">
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Vehicle Details</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <form id="vehicle-form" onSubmit={vehicleForm.handleSubmit(onVehicleSubmit)} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Vehicle Type</Label>
                                            {isKycReviewMode ? (
                                                <Input value={vehicleForm.getValues('vehicleType') || ''} readOnly className="bg-muted" />
                                            ) : (
                                            <Controller
                                                control={vehicleForm.control}
                                                name="vehicleType"
                                                render={({ field }) => (
                                                    <Select value={field.value || ''} onValueChange={(val) => {
                                                        field.onChange(val);
                                                        if (vehicleCategories) {
                                                            const cat = vehicleCategories.find((c) => c.vehicleCategoryName === val);
                                                            if (cat) {
                                                                vehicleForm.setValue('seatingCapacity', cat.maxPassengerCount);
                                                                vehicleForm.setValue('luggageCapacity', cat.maxLuggageVolume);
                                                            }
                                                        }
                                                    }}>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Select type" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {vehicleCategories && vehicleCategories.length > 0 ? (
                                                                vehicleCategories.map((cat) => (
                                                                    <SelectItem key={cat.vehicleCategoryId} value={cat.vehicleCategoryName}>
                                                                        {cat.vehicleCategoryName}
                                                                    </SelectItem>
                                                                ))
                                                            ) : (
                                                                <>
                                                                    <SelectItem value="CAR">Car</SelectItem>
                                                                    <SelectItem value="BIKE">Bike</SelectItem>
                                                                    <SelectItem value="AUTO">Auto</SelectItem>
                                                                </>
                                                            )}
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                            />
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Make</Label>
                                            <Input {...vehicleForm.register('vehicleMake')} readOnly={isKycReviewMode} className={isKycReviewMode ? 'bg-muted' : ''} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Model</Label>
                                            <Input {...vehicleForm.register('vehicleModel')} readOnly={isKycReviewMode} className={isKycReviewMode ? 'bg-muted' : ''} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Registration Number</Label>
                                            <Input {...vehicleForm.register('vehicleRegistrationNumber')} readOnly={isKycReviewMode} className={isKycReviewMode ? 'bg-muted' : ''} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Color</Label>
                                            <Input {...vehicleForm.register('vehicleColor')} readOnly={isKycReviewMode} className={isKycReviewMode ? 'bg-muted' : ''} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Year</Label>
                                            <Input type="number" {...vehicleForm.register('yearOfManufacture')} readOnly={isKycReviewMode} className={isKycReviewMode ? 'bg-muted' : ''} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Seating Capacity</Label>
                                            <Input type="number" {...vehicleForm.register('seatingCapacity')} readOnly className="bg-muted" placeholder="Auto-filled from vehicle type" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Luggage Capacity</Label>
                                            <Input type="number" {...vehicleForm.register('luggageCapacity')} readOnly className="bg-muted" placeholder="Auto-filled from vehicle type" />
                                        </div>
                                    </div>
                                </form>
                            </CardContent>
                            {!isKycReviewMode && (
                            <CardFooter>
                                <Button type="submit" form="vehicle-form" disabled={isUpdatingVehicle}>
                                    {isUpdatingVehicle && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Save Changes
                                </Button>
                            </CardFooter>
                            )}
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Vehicle Documents</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <DocumentViewer
                                    documents={vehicleDocuments}
                                    onDownload={handleDownload}
                                    loading={tempUrlsLoading}
                                    hideActions
                                />
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Banking Tab */}
                <TabsContent value="banking">
                    <Card>
                        <CardHeader>
                            <CardTitle>Banking Information</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <form id="banking-form" onSubmit={bankingForm.handleSubmit(onBankingSubmit)} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Account Holder Name</Label>
                                        <Input {...bankingForm.register('bankAccountHolderName')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Bank Name</Label>
                                        <Input {...bankingForm.register('bankName')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Account Number</Label>
                                        <Input {...bankingForm.register('accountNumber')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>IFSC Code</Label>
                                        <Input {...bankingForm.register('ifscCode')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>SWIFT Code</Label>
                                        <Input {...bankingForm.register('swiftCode')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Payment Frequency</Label>
                                        <Controller
                                            control={bankingForm.control}
                                            name="paymentFrequency"
                                            render={({ field }) => (
                                                <Select value={field.value || ''} onValueChange={field.onChange}>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select frequency" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="WEEKLY">Weekly</SelectItem>
                                                        <SelectItem value="BIWEEKLY">Bi-Weekly</SelectItem>
                                                        <SelectItem value="MONTHLY">Monthly</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        />
                                    </div>
                                </div>
                            </form>
                        </CardContent>
                        <CardFooter>
                            <Button type="submit" form="banking-form" disabled={isUpdatingBanking}>
                                {isUpdatingBanking && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Save Changes
                            </Button>
                        </CardFooter>
                    </Card>
                </TabsContent>

                {/* Activity Tab */}
                <TabsContent value="activity">
                    <Card>
                        <CardHeader>
                            <CardTitle>Device & App Info</CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-muted-foreground">Device Type</p>
                                <p className="font-medium">{driver.deviceType || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">OS Version</p>
                                <p className="font-medium">{driver.osVersion || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">App Version</p>
                                <p className="font-medium">{driver.appVersion || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Last Login</p>
                                <p className="font-medium">{driver.lastLogin || 'N/A'}</p>
                            </div>
                            <div className="col-span-2">
                                <p className="text-sm text-muted-foreground">FCM Token</p>
                                <p className="font-mono text-xs truncate bg-muted p-2 rounded mt-1">
                                    {driver.fcmToken || 'N/A'}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default DriverDetailsPage;
